import {serviceTypes, log, invokeGTag, invokeService} from "@/configs/tracker/utils";
import { commands, semaphore } from "@/utils/flutter/flutter2-semaphore";
import { logPageView } from "@/utils/analytics/usage";

/**
 * Function container for calling analytics methods.
 *
 * @see Functions documentation.
 */
class Tracker {
    /**
     * This function calls the flutter firebase analytics event only.
     *
     * @param {string} propName
     * @param {string|number} propValue
     * @param {object} [eventData]
     * @return {void}
     *
     * @example
     *
     * // case 1
     * Tracker.setUserProperty("tutorial_watched", 1);
     * // case 2
     * Tracker.setUserProperty("tutorial_watched", 1, {
     *     event_name: "tutorial",
     *     event_label: "Tutorial",
     *     event_category: "video",
     *     event_value: 87,
     * });
     */
    static setUserProperty(propName, propValue, eventData) {
        try {
            if (!propName) return log("Missing [propName]");
            if (!propValue) return log("Missing [propValue]");


            if ('SemaphoreJavascriptChannel' in window) {
                semaphore({
                    name: commands.analytics.command,
                    data: {
                    value: commands.analytics.setUserProperty,
                    propertyKey: propName,
                    propertyValue: propValue,
                    },
                });
            } else {
                invokeService({
                    flutterEvent: serviceTypes.setUserProperty,
                    key: propName,
                    value: propValue,
                });
            }

            if (typeof eventData !== "object") return;

            Tracker.logEvent(eventData);

            const gtagParams = {};
            gtagParams[propName] = propValue;
            invokeGTag("set", "user_properties", gtagParams);
        } catch (error) {
            window.captureException(error);
        }
    }

    /**
     * This function calls two other functions, `gtag` and fire a flutter firebase analytics event.
     *
     * @param {object} props
     * @param {string} props.event_name
     * @param {string} props.event_label
     * @param {string} [props.event_category]
     * @param {number} [props.event_value]
     * @param {string} [props.name]
     * @param {string} [props.key] - TODO: remove `key` and `value` after flutter code has been modified
     * @param {string} [props.value] - TODO: remove `key` and `value` after flutter code has been modified
     * @return {void}
     *
     * @example
     *
     * const props = {
     *     event_name: "tutorial",
     *     event_label: "Tutorial",
     *     event_category: "video",
     *     event_value: 87,
     * };
     *
     * Tracker.logEvent(props);
     */
    static logEvent(props) {
        try {
            if (!props) return log("Missing [props]");
            if (typeof props.event_name !== "string") return log("[event_name] must be a string");
            if (typeof props.event_label !== "string") return log("[event_label] must be a string");
            if (typeof props.event_value !== "undefined" && typeof props.event_value !== "number") return log("[event_value] must be a number");
            if (typeof props.event_category !== "undefined" && typeof props.event_category !== "string") return log("[event_category] must be a string");

            // TODO: remove `key` and `value` after flutter code has been modified
            props.key = props.value = props.name = serviceTypes.setUserProperty;

            const config = {
                event_name: props.event_name,
                event_label: props.event_label,
            };

            if (typeof props.event_value === "number") config["event_value"] = props.event_value;
            if (typeof props.event_category === "string") config["event_category"] = props.event_category;


            if ('SemaphoreJavascriptChannel' in window) {
                semaphore({
                    name: commands.analytics.command,
                    data: {
                    value: commands.analytics.logEvent,
                    eventName: props.event_name,
                    eventData: props,
                    },
                });
            } else {
                invokeService({
                    flutterEvent: 'logEvent',
                    name: props.event_name,
                    data: props,
                });
            }

            if (typeof props.event_value === "number") config["value"] = props.event_value;

            invokeGTag("event", props.event_name, config);
        } catch (error) {
            window.captureException(error);
        }
    }

    /**
     * This function calls two other functions, `gtag` and fire a flutter firebase analytics event.
     *
     * @param {string} screenName
     * @return {void}
     *
     * @example
     *
     * Tracker.screenView("/");
     */
    static screenView(screenName) {
        try {
            if (!screenName) return log("Missing [screenName] property");

            const props = {};

            props.name = serviceTypes.setCurrentScreen;

            // TODO: remove this line after flutter code has been modified
            props.key = props.value = screenName;

            const config = {
                event_category: "screen",
                event_label: screenName,
            };

            if ('SemaphoreJavascriptChannel' in window) {
                semaphore({
                    name: commands.analytics.command,
                    data: {
                    value: commands.analytics.setCurrentScreen,
                    screenName: screenName,
                    },
                });
            } else {
                invokeService({
                    flutterEvent: 'setCurrentScreen',
                    screen: screenName
                });
            }
            invokeGTag("event", "view", config);

            logPageView(screenName);
        } catch (error) {
            window.captureException(error);
        }
    }
}

export default Tracker;
